import { Component, OnInit } from '@angular/core';
import { SommelierService } from '@proxy/sommeliers';
import { OrderService } from '@proxy/orders';
import { firstValueFrom } from 'rxjs';
import { OrderStatus } from '@proxy/enums';
import { ConfigStateService } from '@abp/ng.core';
import { Router } from '@angular/router';
import { SelectedSommelierService } from '../shared/services/selected-sommelier.service';
//test
type PeriodType = 'last30days' | 'thisMonth' | 'thisYear';

@Component({
  selector: 'app-my-numbers',
  templateUrl: './my-numbers.component.html',
  standalone: false
})
export class MyNumbersComponent implements OnInit {
  selectedPeriod: PeriodType = 'last30days';
  loading = false;
  hasPickupRole = false;

  kpiData = {
    sales: 0,
    clients: 0,
    deliveries: 0
  };

  constructor(
    private sommelierService: SommelierService,
    private orderService: OrderService,
    private configStateService: ConfigStateService,
    private router: Router,
    private selectedSommelierService: SelectedSommelierService
  ) { }

  async ngOnInit(): Promise<void> {
    // Verificar si el usuario tiene el rol pickup
    const currentUser = await firstValueFrom(this.configStateService.getOne$('currentUser'));
    const roles = (currentUser as any)?.roles ?? [];
    this.hasPickupRole = roles.includes('pickup');

    this.loadData();
  }

  setPeriod(period: PeriodType): void {
    this.selectedPeriod = period;
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.loading = true;
    try {
      // Obtener filtros de fecha según el período seleccionado
      const dateFilter = this.getDateFilter();

      // Obtener cantidad de clientes del sommelier con los mismos filtros que la vista
      const clientsCount = await firstValueFrom(
        this.sommelierService.getMyClientsCount({
          from: dateFilter.startDate.toISOString(),
          to: dateFilter.endDate.toISOString()
        } as any)
      );
      this.kpiData.clients = clientsCount;

      // Obtener cantidad de ventas (órdenes pagadas) con los mismos filtros
      const salesCount = await firstValueFrom(
        this.sommelierService.getMySalesCount({
          from: dateFilter.startDate.toISOString(),
          to: dateFilter.endDate.toISOString()
        } as any)
      );
      this.kpiData.sales = salesCount;

      // Obtener órdenes del sommelier solo para calcular entregas (si tiene rol pickup)
      if (this.hasPickupRole) {
        // Obtener todas las órdenes usando paginación (máximo 1000 por request)
        let allOrders: any[] = [];
        let skipCount = 0;
        const maxResultCount = 1000; // Límite del servidor
        let hasMore = true;

        while (hasMore) {
          const response = await firstValueFrom(
            this.orderService.getSommelierOrders({
              skipCount: skipCount,
              maxResultCount: maxResultCount,
              sorting: 'creationTime DESC'
            })
          );

          allOrders = allOrders.concat(response.items);

          // Si recibimos menos resultados que el máximo, no hay más páginas
          hasMore = response.items.length === maxResultCount;
          skipCount += maxResultCount;
        }

        // Filtrar órdenes por período para entregas
        const filteredOrders = allOrders.filter(order => {
          if (!order.creationTime) return false;
          const orderDate = new Date(order.creationTime);
          return orderDate >= dateFilter.startDate && orderDate <= dateFilter.endDate;
        });

        // Contar entregas solo si el usuario tiene el rol pickup
        this.kpiData.deliveries = filteredOrders.filter(
          order => order.orderStatus === OrderStatus.Delivered
        ).length;
      } else {
        this.kpiData.deliveries = 0;
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      this.loading = false;
    }
  }

  getPeriodLabel(period: PeriodType): string {
    switch (period) {
      case 'last30days':
        return '::Last30Days';
      case 'thisMonth':
        return '::ThisMonth';
      case 'thisYear':
        return '::ThisYear';
      default:
        return '';
    }
  }
 
  onSalesCardClick(): void {
    const { start, end } = this.getQueryDateRange();
 
    const extras = {
      queryParams: {
        start,
        end,
        status: OrderStatus.Paid // Filtrar solo órdenes pagadas (ventas)
      }
    };
 
    const slug = this.selectedSommelierService.currentSlugValue;
    if (slug) {
      this.router.navigate([`/${slug}/sommelier-orders`], extras);
    } else {
      // Si no hay slug, intentar obtenerlo de la ruta actual
      const currentUrl = this.router.url;
      const slugMatch = currentUrl.match(/^\/([^\/]+)/);
      if (slugMatch && slugMatch[1]) {
        this.router.navigate([`/${slugMatch[1]}/sommelier-orders`], extras);
      }
    }
  }
 
  onClientsCardClick(): void {
    const { start, end, period } = this.getQueryDateRange();
 
    const extras = {
      queryParams: {
        start,
        end,
        period
      }
    };
 
    const slug = this.selectedSommelierService.currentSlugValue;
    if (slug) {
      this.router.navigate([`/${slug}/sommelier-clients`], extras);
    } else {
      const currentUrl = this.router.url;
      const slugMatch = currentUrl.match(/^\/([^\/]+)/);
      if (slugMatch && slugMatch[1]) {
        this.router.navigate([`/${slugMatch[1]}/sommelier-clients`], extras);
      }
    }
  }
 
  onDeliveriesCardClick(): void {
    const { start, end, period } = this.getQueryDateRange();
 
    const extras = {
      queryParams: {
        start,
        end,
        period
      }
    };
 
    const slug = this.selectedSommelierService.currentSlugValue;
    if (slug) {
      this.router.navigate([`/${slug}/deliveries`], extras);
    } else {
      const currentUrl = this.router.url;
      const slugMatch = currentUrl.match(/^\/([^\/]+)/);
      if (slugMatch && slugMatch[1]) {
        this.router.navigate([`/${slugMatch[1]}/deliveries`], extras);
      }
    }
  }
 
  private getDateFilter(): { startDate: Date; endDate: Date } {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date();
 
    switch (this.selectedPeriod) {
      case 'last30days':
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'thisMonth':
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'thisYear':
        startDate.setMonth(0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
    }
 
    return { startDate, endDate };
  }
 
  private getQueryDateRange(): { start: string; end: string; period: PeriodType } {
    const { startDate, endDate } = this.getDateFilter();
    const pad = (n: number) => String(n).padStart(2, '0');
    const format = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
 
    return {
      start: format(startDate),
      end: format(endDate),
      period: this.selectedPeriod
    };
  }
}


